import { useEffect, useMemo, useRef } from 'react';
import { transform } from 'ol/proj';
import { useOpenLayersContext } from '@ais-ui/react-openlayers';
import { formatCoordinate } from '@ais-ui/utils';
import { useSettings } from '../Settings/SettingsProvider';
import { useAISMapContext } from './AISMapContext';
import { throttle } from 'lodash';

export function useCursorCoordinates() {
  const { map, displayProjection, dataProjection } = useOpenLayersContext();
  const { settings } = useSettings();
  const { cursorPosition, setCursorPosition } = useAISMapContext();
  const isDragging = useRef(false);

  const convertCoordinates = useMemo(() => {
    const format = settings['ribbonPlugin.settings']?.cursor_position_format;

    return (coords: number[]) => {
      return formatCoordinate(coords[0], coords[1], format);
    };
  }, [settings['ribbonPlugin.settings']?.cursor_position_format]);

  // Handle drag states
  useEffect(() => {
    if (!map) return;

    const handleMoveStart = () => {
      isDragging.current = true;
    };

    const handleMoveEnd = () => {
      isDragging.current = false;
    };

    map.on('movestart', handleMoveStart);
    map.on('moveend', handleMoveEnd);

    return () => {
      map.un('movestart', handleMoveStart);
      map.un('moveend', handleMoveEnd);
    };
  }, [map]);

  // Use refs to avoid recreating the throttled function
  const convertCoordinatesRef = useRef(convertCoordinates);
  const setCursorPositionRef = useRef(setCursorPosition);
  const mapRef = useRef(map);
  const displayProjectionRef = useRef(displayProjection);
  const dataProjectionRef = useRef(dataProjection);
  const settingsRef = useRef(settings);

  // Update refs when values change
  useEffect(() => {
    convertCoordinatesRef.current = convertCoordinates;
    setCursorPositionRef.current = setCursorPosition;
    mapRef.current = map;
    displayProjectionRef.current = displayProjection;
    dataProjectionRef.current = dataProjection;
    settingsRef.current = settings;
  });

  // Create a throttled handler that checks for dragging
  const handleMouseMove = useMemo(
    () =>
      throttle((event: MouseEvent) => {
        if (
          settingsRef.current['ribbonPlugin.settings']?.show_cursor_position !==
          true
        )
          return;
        if (isDragging.current) return; // Skip updates while dragging, as they cause re-renders of map overlay

        if (!mapRef.current) return;

        const coordinate = mapRef.current.getEventCoordinate(event);

        if (coordinate) {
          // Convert from map projection to lon/lat
          const [longitude, latitude] = transform(
            coordinate,
            displayProjectionRef.current,
            dataProjectionRef.current,
          );

          setCursorPositionRef.current(
            convertCoordinatesRef.current([longitude, latitude]),
          );
        }
      }, 50),
    [], // Empty dependency array - function is stable, uses refs internally
  );

  useEffect(() => {
    window.addEventListener('mousemove', handleMouseMove);
    return () => {
      handleMouseMove.cancel();
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, [handleMouseMove]);

  return cursorPosition;
}
